import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import LoadingButton from "@mui/lab/LoadingButton";
import logoIcon from "assets/images/logos/fusionmsi-logo.svg";
import Loading from "components/Loading";
import RecordButton from "components/dashboard/chartmind/common/RecordButton";
import RecordingClock from "components/dashboard/chartmind/common/RecordingClock";
import useScreenWakeLock from "hooks/useScreenWakeLock";
import { useAuth } from "hooks/useAuth";
import {
  redeemDeviceLinkGrant,
  signInWithDeviceLinkToken,
} from "services/deviceLinkService";
import useEncounterRecording from "hooks/useEncounterRecording";
import {
  PHONE_RELAY_FLUSH_MS,
  PHONE_RELAY_MAX_WAIT_MS,
  PHONE_RELAY_STATUS,
  claimPhoneRelay,
  connectPhoneRelay,
  getPhoneRelay,
  subscribePhoneRelay,
  writePhoneCommand,
  writePhoneTranscript,
} from "services/chartMindPhoneRelayService";

/**
 * The phone half of "Record on your phone".
 *
 * WHY THIS ROUTE IS FLAT AND NOT UNDER /dashboard. The dashboard shell mounts Stripe Elements,
 * LocationProvider and — for professionals — ChartMindSessionProvider. Mounting that here would
 * give the phone its OWN ChartMind session: a second recognizer, a second clarifying-questions
 * poll billing LLM calls for one encounter, and a second Firestore autosave path. The phone
 * needs none of it. It records, it flushes text, it renders what the desktop sends down.
 *
 * ⚠️ WHAT THIS PAGE DELIBERATELY CANNOT DO. It never reads a ChartMind step, a diagnosis or a
 * patient record, and it never writes anything but the transcript. It is a microphone with a
 * screen, not a second copy of the app. See CHARTMIND-PHONE-MIC.md for why full mirroring was
 * rejected.
 *
 * The transcript view is read-only on purpose: nobody edits a transcript on a phone, and it
 * means the both-devices-editing problem cannot arise rather than having to be resolved.
 */

/**
 * The heading for every phase of a live pairing.
 *
 * ⚠️ IT DOES NOT CHANGE WITH THE PHASE, and that is deliberate. It used to: "Ready to record",
 * then "Recording", then "Sent to your computer". A heading that rewords itself is the
 * quietest possible way to announce a state change, and this screen is being glanced at by
 * someone whose attention is on a patient. So the heading holds still and says where you are,
 * and anything that CHANGED says so in an alert, which is the thing that actually gets
 * noticed. The dead-end screens below (not signed in, no pairing, no microphone) keep their
 * own headings: you arrive on those, you do not transition into them.
 */
const PAIRING_TITLE = "Record on your phone";

/**
 * What this panel's BUTTONS are made of, and nothing else.
 *
 * ⚠️ THE RELEASE CONDITION FOR A PRESSED BUTTON, so it deliberately ignores `nextEnabled`,
 * `blockedReason` and the rest. Releasing on any change at all meant a remote that touched one
 * of those while the step stayed put counted as the desktop's answer: the spinner stopped, the
 * button snapped back to the step the clinician had just left, and a second later the real next
 * step arrived and it changed again. Two labels and a flicker for one press.
 */
const remoteButtonKey = (remote) =>
  [
    remote?.stepLabel || "",
    remote?.nextLabel || "",
    remote?.skipLabel || "",
    remote?.mode || "step",
  ].join("|");

/** The note section a pushed remote is armed for, or "" for the encounter. See dictateArmRef. */
const dictateArmKey = (remote) =>
  remote?.mode === "dictate" ? remote.dictateLabel || "this section" : "";

/**
 * How long the phone waits for the desktop to answer a Next before releasing the button.
 *
 * Generous on purpose: advancing a step kicks off an LLM run on the desktop, and the push back
 * comes after that render. Too short and the button frees itself while the request is still in
 * flight, inviting a second press that advances two steps.
 */
const COMMAND_ANSWER_TIMEOUT_MS = 8000;

/**
 * When to go and ask, after a press that has not been answered.
 *
 * Inside COMMAND_ANSWER_TIMEOUT_MS on purpose, so a snapshot that never arrives is corrected
 * while the button is still spinning rather than after it has given up. Two attempts: the
 * first is for a lost snapshot, the second for a desktop that was slow to answer.
 */
const COMMAND_RESYNC_DELAYS_MS = [1500, 4000];

const PageShell = ({ title, subtitle, children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: "background.paper",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      pt: 4,
    }}
  >
    <Link component={RouterLink} to="/">
      <Box component="img" src={logoIcon} alt="Fusion MSI" sx={{ height: 48, mb: 2 }} />
    </Link>
    <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", p: 3 }}>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mb: subtitle ? 1 : 3, textAlign: "center" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  </Box>
);

/**
 * THE WAY OUT OF RECORD-ONLY MODE.
 *
 * Without this the phone is a dead end: it records, it says thank you, and the only exit is
 * closing the tab. That reads as "this phone can do one thing", which is wrong. ChartMind runs
 * perfectly well on a phone, so this is a link, not a feature.
 *
 * ⚠️ IT NEVER APPEARS MID-ENCOUNTER. Offering to abandon a live recording is the wrong thing
 * to put under a thumb, and taking it would mean stopping, flushing and releasing the pairing
 * first, or the desktop sits on "recording on your phone" forever.
 *
 * It starts a SEPARATE encounter and does not carry the current one over: that would be a
 * session handoff, which needs the whole serialized session on the wire. See
 * CHARTMIND-PHONE-MIC.md.
 */
const NewEncounterLink = ({ caption }) => (
  <Stack spacing={1} alignItems="center" sx={{ width: "100%" }}>
    <Button component={RouterLink} to="/dashboard/chartmind" fullWidth>
      Start a new encounter on this phone
    </Button>
    {caption && (
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
        {caption}
      </Typography>
    )}
  </Stack>
);

/**
 * THE PHONE AS A REMOTE FOR THE DESKTOP.
 *
 * ⚠️ IT RENDERS WHAT THE DESKTOP SENDS AND NOTHING ELSE. Every label here — the step, the button
 * text, the reason Next cannot fire — was written by the desktop (writeRemoteState). This
 * component computes no clinical state, reads no diagnosis, and decides nothing about whether
 * the press is allowed; it sends a verb and the desktop re-checks its own gate. That is the
 * whole difference between a remote and a mirror, and it is what keeps this screen safe to
 * leave face-up on a counter.
 *
 * A disabled Next says what to do rather than sitting there greyed out. On the desktop the
 * reason is on screen — the diagnoses you have not picked are right above the button. Here
 * there is nothing around it, so the reason has to travel with it.
 *
 * ⚠️ AN EMPTY stepLabel MEANS RENDER NOTHING; a label with no buttons means render the line
 * alone. The desktop draws that distinction (see phoneRemote) and this component only obeys it.
 * The silent case is the Record step before a round has landed, where the only thing to say
 * would be about the microphone already filling the screen. The line-alone case is the Chart
 * step, where there is genuinely nowhere left to go and the clinician still wants to know that.
 *
 * Borderless on purpose. It sits between a big microphone and a divider, and a card around it
 * made a third box on a screen that is already mostly boxes; the contained button is its own
 * anchor. The line is primary-coloured with the step name in bold instead, because as plain
 * secondary text it read as one more caption among the captions around it.
 */
const DesktopRemote = ({ remote, onCommand, sendingVerb }) => {
  const stepLabel = remote?.stepLabel || "";
  const nextLabel = remote?.nextLabel || "";
  const skipLabel = remote?.skipLabel || "";
  // A dictation owns the screen; DictationBanner speaks for the desktop instead.
  if (remote?.mode === "dictate") return null;
  if (!stepLabel) return null;

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Typography variant="body2" sx={{ textAlign: "center" }}>
        Your computer is on the{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {stepLabel}
        </Box>{" "}
        step.
      </Typography>

      {nextLabel && (
        <Box>
          <LoadingButton
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardRounded />}
            disabled={!remote.nextEnabled}
            loading={sendingVerb === "next"}
            onClick={() => onCommand("next")}
          >
            {nextLabel}
          </LoadingButton>
          {!remote.nextEnabled && remote.blockedReason && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              {remote.blockedReason}
            </Typography>
          )}
        </Box>
      )}

      {skipLabel && (
        <LoadingButton
          fullWidth
          disabled={!remote.skipEnabled}
          loading={sendingVerb === "skip"}
          onClick={() => onCommand("skip")}
        >
          {skipLabel}
        </LoadingButton>
      )}
    </Stack>
  );
};

/**
 * THE PHONE AS A MICROPHONE FOR ONE PART OF THE NOTE.
 *
 * ⚠️ IT IS TOLD THE SECTION'S NAME, NOT THE SECTION. `dictateLabel` is a string to print; this
 * phone holds no note, no section list, and no way to choose a different one. The desktop opened
 * that editor and the desktop routes the round — exactly the remote/mirror line the step buttons
 * above are drawn on.
 */
const DictationBanner = ({ remote, state }) => {
  if (remote?.mode !== "dictate") return null;
  const label = remote.dictateLabel || "your note";

  // ⚠️ THE PHONE IS POINTED AT A NOTE SECTION ON EVERY SCREEN OF THIS MODE, not only while a
  // round runs. This is the one place in ChartMind where the microphone writes somewhere other
  // than the encounter, and the question it answers — "where do my words go if I press this" —
  // is live between rounds too, which is exactly when the clinician looks down at a phone they
  // put in a pocket three minutes ago. A green "delivered" alert answers a question they were
  // not asking and says nothing about the next press.
  //
  // ⚠️ "armed" IS INFO AND SAYS NOTHING HAS HAPPENED YET, and that distinction is the bug this
  // separation fixes. A phone that has just been handed a section has recorded nothing into it,
  // and every version of this that wore the post-round wording there told the clinician their
  // words had already been added before they had said one — on the screen whose whole job is to
  // invite the first press. Yellow starts once there is something to be careful about: a round
  // running, or a round already in the section and another press about to join it.
  const armed = state === "armed";
  return (
    <Alert
      severity={armed ? "info" : "warning"}
      icon={<EditNoteRounded />}
      sx={{ width: "100%" }}
    >
      {state === "recording"
        ? `Recording into ${label} in your note.`
        : armed
          ? `Tap the microphone to add something to ${label} in your note.`
          : `This will record into ${label} in your note.`}
    </Alert>
  );
};

const PhoneRecordingPage = () => {
  const [searchParams] = useSearchParams();
  const pairingId = searchParams.get("p") || "";
  const { user, userLoading } = useAuth();

  // "loading" -> "expired" | "wrongAccount" | "ready" | "recording" | "done"
  const [phase, setPhase] = useState("loading");
  const [relayLanguage, setRelayLanguage] = useState("en-US");
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  // Where the desktop is and what its Next would do. Pushed down by the desktop; this page
  // renders it and never computes it. Null until the first push lands.
  const [remote, setRemote] = useState(null);
  // True from the moment stop is pressed until the final transcript write has been answered.
  // See stop() for why pendingSegments cannot carry this on its own.
  const [finalizing, setFinalizing] = useState(false);
  /**
   * Has THIS round's claim landed on the document?
   *
   * ⚠️ WITHOUT THIS, A STALE `stopping` STOPS THE NEXT ROUND THE INSTANT IT STARTS. Pressing
   * record flips the screen to recording immediately, but the claim that moves the document to
   * `recording` is a round trip behind it. Any snapshot delivered in that gap still carries the
   * status the document had before the press — and if that status is `stopping`, the handler
   * below dutifully stops a round that has not begun. The clinician sees the button light up and
   * fall straight back, presses again, and the second press works because the claim has landed
   * by then. Answering only stop requests that arrive after our own claim closes the window.
   */
  const claimedRef = useRef(false);
  // A stale `stopping` is answered exactly once, so that clearing it cannot turn into a loop
  // with a desktop that re-requests.
  const staleStopAnsweredRef = useRef(false);
  // The same, for the ordinary stop: one answer per stopping episode. This listener sees its
  // own writes now, so an unguarded answer would answer itself. See the handler below.
  const stopAnsweredRef = useRef(false);
  // The remote as last rendered, so a snapshot that changed something else does not re-render
  // the screen, and a snapshot that DID change the remote releases the pressed button.
  const remoteJsonRef = useRef(null);
  // The buttons as they were when one of them was pressed, or null when nothing is outstanding.
  const pressedKeyRef = useRef(null);
  /**
   * Which note section this phone is currently armed for, or "" for the encounter.
   *
   * ⚠️ IT EXISTS TO SPOT A *NEW* ARMING, which is the one thing the status on the wire cannot
   * say. Handing the phone a section does not change the pairing's status, so a phone sitting on
   * the post-round screen stayed there — and that screen's entire vocabulary is "add MORE", on
   * top of the alert saying the last round had arrived. A clinician who had just pressed
   * "Dictate on your phone" was therefore told their words were already in the section before
   * they had said any. A fresh arming is a fresh round waiting to be spoken, so the phone goes
   * back to its ready screen for it, exactly as it does when the desktop re-arms the pairing for
   * a new encounter.
   */
  const dictateArmRef = useRef("");
  // Which verb is in flight, from the tap until the desktop's answer comes back as a changed
  // step. The round trip is a Firestore write, a desktop render and a push back, which is long
  // enough that without feedback the button reads as dead and gets pressed again. Held as the
  // verb rather than a boolean so only the button that was pressed spins.
  const [sendingVerb, setSendingVerb] = useState(null);
  const [relayError, setRelayError] = useState("");
  // True from the tap until the microphone is actually open. Drives the record button's busy
  // state, which is what stops a second tap reaching Stop while the permission prompt is up.
  const [starting, setStarting] = useState(false);
  const startingRef = useRef(false);

  const recording = useEncounterRecording({ language: relayLanguage });
  const { micLevel } = recording;

  // Derived up here rather than beside the render branches below, because the two hooks that
  // need it cannot sit after an early return.
  const isRecording = phase === "recording";

  /**
   * ⚠️ WITHOUT THIS THE FEATURE FAILS ON EVERY REAL ENCOUNTER. A phone blanks its display
   * after 30 to 60 seconds and audio capture stops with it, so recording previously survived
   * only as long as somebody kept tapping the screen. `held` is false when the browser has no
   * wake lock or refuses one, which is the only case where the clinician has to keep the
   * screen awake themselves and the only case worth telling them about.
   */
  const { held: screenHeld } = useScreenWakeLock(isRecording);

  /**
   * ⚠️ NO IDLE BLUR ON THIS PAGE, AND THAT IS A DECISION RATHER THAN AN OMISSION.
   *
   * The desktop has one (see ChartMindPage) because it shows the verbatim transcript, the
   * differential and the chart note. This page shows a mic meter, a timer, a word count and
   * the clarifying questions, and the transcript was deliberately taken off it. None of the
   * first three says anything about the patient.
   *
   * That leaves the questions, and the exposure they carry does not survive examination. They
   * only ever render while the pairing is live and the clinician is in the room; the finished
   * screen does not render them at all, so a phone left behind after an encounter is already
   * showing nothing. The audience for a phone face-up on a counter mid-encounter is the
   * patient, reading prompts about their own visit.
   *
   * Set against that, a cover here has a real cost: it hides the questions at the exact moment
   * they are meant to be glanced at, and this page's whole job is to be glanceable. Removing
   * the transcript already did the privacy work a blur would have done. Defending by having
   * nothing on the screen beats defending by covering it.
   */

  /**
   * ⚠️ RELEASE THE BUTTON EVEN IF THE DESKTOP SAYS NOTHING BACK.
   *
   * Normally a changed step label is the answer. But the desktop deliberately ignores a Next
   * whose gate no longer passes — pressed just as the last diagnosis came off — and an ignored
   * press produces no push at all. Without this the button would spin forever on the one case
   * the design is built to allow.
   */
  useEffect(() => {
    if (!sendingVerb) return undefined;
    const timer = setTimeout(() => {
      pressedKeyRef.current = null;
      setSendingVerb(null);
    }, COMMAND_ANSWER_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [sendingVerb]);

  const lastFlushedRef = useRef("");
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  /**
   * Sign this phone in from the scan itself, if the desktop sent a grant along.
   *
   * ⚠️ THIS IS THE WHOLE POINT OF THE FEATURE, so it runs before anything asks the person to do
   * something. Making a clinician type a password and an MFA code into a phone, in front of a
   * patient, to reach a microphone would cost more friction than the bad desk mic it replaces.
   *
   * Runs once, guarded by a ref: redeeming is single-use and destructive server-side (the grant
   * is deleted before the token is minted), so a re-render must not retry it and turn a working
   * sign-in into "that code has expired".
   *
   * On failure it falls through to the manual sign-in below, which then says that the automatic
   * one did not work. It is never an error screen of its own: a phone that is already signed in
   * needs nothing from the grant, and the sign-in screen is only reached by a phone that does.
   * But saying nothing at all was worse — the desktop had just promised a scan-and-go, so the
   * password prompt read as the feature being broken rather than as one thing to do.
   */
  const grantId = searchParams.get("g") || "";
  const grantSecret = searchParams.get("s") || "";
  const redeemedRef = useRef(false);
  const [redeeming, setRedeeming] = useState(Boolean(grantId && grantSecret));
  const [grantFailed, setGrantFailed] = useState(false);

  useEffect(() => {
    if (userLoading) return;
    if (!grantId || !grantSecret) return;
    if (user && !user.isAnonymous) {
      // Already signed in, so the grant is not needed. Leave it to expire unspent.
      setRedeeming(false);
      return;
    }
    if (redeemedRef.current) return;
    redeemedRef.current = true;

    (async () => {
      try {
        const { customToken } = await redeemDeviceLinkGrant({ grantId, grantSecret });
        await signInWithDeviceLinkToken(customToken);
      } catch {
        // Fall through to the manual sign-in path, which says so.
        setGrantFailed(true);
      } finally {
        setRedeeming(false);
      }
    })();
  }, [userLoading, user, grantId, grantSecret]);

  /**
   * If we end up asking for a manual sign-in anyway, come straight back here afterwards.
   *
   * Same mechanism the share flow uses (ShareLandingPage -> AuthPage -> DashboardPage). The
   * pairing id is carried but the grant is deliberately NOT: by this point it is spent or
   * broken, and putting a credential into localStorage to survive a redirect would be a worse
   * trade than one password entry.
   */
  useEffect(() => {
    if (userLoading || redeeming || !pairingId) return;
    if (user && !user.isAnonymous) return;
    try {
      localStorage.setItem("pendingShareReturn", `/chartmind-mic?p=${pairingId}`);
    } catch {
      // Private mode. The manual sign-in link below still works.
    }
  }, [userLoading, redeeming, user, pairingId]);

  /**
   * Take a remote from the desktop, wherever it came from.
   *
   * One path for the first read, every snapshot, and the re-reads below, because the rule is
   * the same in all three: a changed remote IS the desktop's answer to a press, so it is also
   * what releases the button. There is no ack field to keep in sync.
   */
  const applyRemote = useCallback((next) => {
    const json = JSON.stringify(next ?? null);
    if (json === remoteJsonRef.current) return;
    remoteJsonRef.current = json;
    setRemote(next);
    // The press is answered when the buttons themselves change, not when anything changes.
    // See remoteButtonKey.
    if (
      pressedKeyRef.current !== null &&
      remoteButtonKey(next) !== pressedKeyRef.current
    ) {
      pressedKeyRef.current = null;
      setSendingVerb(null);
    }
  }, []);

  const applyRemoteRef = useRef(applyRemote);
  useEffect(() => {
    applyRemoteRef.current = applyRemote;
  }, [applyRemote]);

  /**
   * ⚠️ ASK, RATHER THAN WAIT FOR A SNAPSHOT THAT MAY NOT COME.
   *
   * The remote is the one thing on this screen the clinician acts on, and every version of it
   * that was delivery-only got stuck sooner or later: a phone that locks or backgrounds while
   * they look at the computer drops its listener, and a listener that reconnects to a document
   * whose contents have not changed since it last saw them is told nothing. The button then
   * names a step the desktop left a minute ago, which is worse than a blank one — it is an
   * invitation to press the wrong thing.
   *
   * So the phone re-reads the document whenever it comes back to the foreground, and again if
   * a press goes unanswered for a moment. One document read, on a screen that is otherwise
   * doing nothing.
   */
  const resyncRemote = useCallback(() => {
    if (!pairingId) return;
    getPhoneRelay(pairingId)
      .then((relay) => {
        if (relay) applyRemoteRef.current(relay.remote);
      })
      .catch(() => {
        // The subscription owns the "this pairing is gone" verdict. A failed refresh is not one.
      });
  }, [pairingId]);

  useEffect(() => {
    if (!pairingId) return undefined;
    const refresh = () => {
      if (document.visibilityState === "visible") resyncRemote();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [pairingId, resyncRemote]);

  // A press with no answer yet. Cleared the moment one lands, because `sendingVerb` is what
  // this keys on and applyRemote clears it.
  useEffect(() => {
    if (!sendingVerb || !pairingId) return undefined;
    const timers = COMMAND_RESYNC_DELAYS_MS.map((delay) =>
      setTimeout(resyncRemote, delay),
    );
    return () => timers.forEach(clearTimeout);
  }, [sendingVerb, pairingId, resyncRemote]);

  /**
   * Is the pairing still live? Asked once, when this screen opens.
   *
   * ⚠️ IT RUNS MORE THAN ONCE, AND IT MUST NOT TOUCH A ROUND IN PROGRESS. This effect keys on
   * the auth state, and signing in on the phone settles in two beats rather than one — the
   * user arrives, then `userLoading` goes back up while their profile is fetched. So this can
   * re-run at any moment, including seconds into a recording.
   *
   * It used to answer with `setPhase("ready")` unconditionally, which was the bug the clinician
   * saw on production: the microphone stayed live and the timer kept climbing, but the button
   * went back to its idle colour and the flush effect below — which only runs while the phase
   * is "recording" — stopped sending, so the desktop never heard a word. Pressing again claimed
   * the pairing at last, and the two timers were half a minute apart for the rest of the
   * encounter.
   *
   * The liveness answer is therefore only allowed to move a screen that has not started yet.
   */
  useEffect(() => {
    if (userLoading || redeeming || !user || user.isAnonymous || !pairingId) return;
    let cancelled = false;

    getPhoneRelay(pairingId)
      .then((relay) => {
        if (cancelled) return;
        // A missing document and one we are not allowed to read are the same answer on
        // purpose, so a guessed id tells the holder nothing. permission-denied lands in the
        // catch below and says the same thing.
        if (!relay) {
          // The pairing really is gone, which is worth saying whatever the screen was doing.
          setPhase("expired");
          return;
        }
        setRelayLanguage(relay.language);
        setClarifyingQuestions(relay.clarifyingQuestions);
        applyRemote(relay.remote);
        // Whatever it is armed for on arrival is the baseline, not a new arming: this phone has
        // only just opened the pairing and has nothing to be put back to.
        dictateArmRef.current = dictateArmKey(relay.remote);
        // Only the opening screen. A later re-run has nothing to tell a phase that has since
        // been decided by the clinician pressing something — see the header above.
        if (phaseRef.current === "loading") {
          setPhase(relay.status === PHONE_RELAY_STATUS.STOPPED ? "done" : "ready");
        }
        // Tell the desktop the scan worked, so it can acknowledge it and drop the QR rather
        // than sitting on a code that has already been used. Fire and forget: failing to say
        // hello must not stop this phone from recording.
        if (relay.status === PHONE_RELAY_STATUS.WAITING) {
          connectPhoneRelay(pairingId).catch(() => {});
        }
      })
      .catch((err) => {
        // Same rule, for the same reason: a re-run that cannot read the document is not
        // grounds to end a recording that is already running. A pairing that has genuinely
        // gone away still arrives as the subscription's own `!relay` above.
        if (cancelled || phaseRef.current !== "loading") return;
        // ⚠️ DENIED LEADS WITH THE ACCOUNT, NOT A DEAD CODE. A pairing past its expiry is
        // still readable by its owner and comes back as `!relay` above. Denied means this
        // phone is signed in as someone else (a patient account left over from a discharge
        // scan, say) OR the document is gone (tab closed, swept): the rules deny a missing
        // doc too, so the two cannot be told apart and the copy names both. Saying only
        // "expired" sent people back for a new code that failed the same way.
        setPhase(err?.code === "permission-denied" ? "wrongAccount" : "expired");
      });

    return () => {
      cancelled = true;
    };
  }, [userLoading, redeeming, user, pairingId]);

  const flushTranscript = useDebouncedCallback(
    (text) => {
      if (!pairingId || text === lastFlushedRef.current) return;
      lastFlushedRef.current = text;
      writePhoneTranscript(pairingId, text).catch(() => {
        // Firestore queues writes offline and replays them, so a blip needs no error here.
        // A genuine failure shows up as the desktop going quiet, which the clinician can see.
      });
    },
    PHONE_RELAY_FLUSH_MS,
    { maxWait: PHONE_RELAY_MAX_WAIT_MS },
  );

  useEffect(() => {
    if (phase !== "recording") return;
    flushTranscript(recording.transcript || "");
  }, [recording.transcript, phase, flushTranscript]);

  const stop = useCallback(async () => {
    if (phaseRef.current !== "recording") return;
    setPhase("done");
    // The round is over, so any later `stopping` is by definition not about it.
    claimedRef.current = false;
    // ⚠️ SET BEFORE THE AWAIT, CLEARED AFTER THE FINAL WRITE. `phase` flips to "done" the
    // instant the button is pressed, but nothing has been sent yet — and pendingSegments is
    // still 0 at that moment, because the recorder has not cut the tail segment. Reading
    // pendingSegments alone therefore announced "Added to your note" in the gap, before a
    // single word had left the phone.
    setFinalizing(true);
    await recording.stopRecording();
    flushTranscript.cancel();
    // The ref, not React state: recognition.onend can land after the last state flush, and
    // this is the write the desktop actually consumes. Same reason handleMicClick reads it.
    const finalText = recording.getFinalTranscript();
    lastFlushedRef.current = finalText;
    try {
      await writePhoneTranscript(pairingId, finalText, { final: true });
    } catch (err) {
      setRelayError(
        err?.message || "Could not send the recording. Please check your connection.",
      );
    } finally {
      setFinalizing(false);
    }
  }, [recording, flushTranscript, pairingId]);

  /**
   * Watch the relay: the desktop pushes clarifying questions down, and can ask us to stop.
   *
   * ⚠️ THE DEPENDENCIES HERE ARE LOAD-BEARING. `stop` is rebuilt whenever the recording hook's
   * return value changes, which is every render — and the mic meter re-renders this page about
   * twelve times a second while recording. Depending on it directly tore down and re-subscribed
   * the Firestore listener at that rate, which is both wasteful and a real chance of missing the
   * desktop's `stopping` write in one of the gaps. So `stop` goes through a ref, and the effect
   * keys on whether it should be watching at all rather than on the exact phase.
   */
  const stopRef = useRef(stop);
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  // Same treatment, and for the same reason: the subscription below has to hand the recorder
  // back to zero when the desktop re-arms the pairing, without re-subscribing every frame the
  // mic meter renders.
  const resetRecordingRef = useRef(recording.resetRecording);
  useEffect(() => {
    resetRecordingRef.current = recording.resetRecording;
  }, [recording.resetRecording]);

  const shouldWatch = Boolean(pairingId) && phase !== "loading" && phase !== "expired";

  useEffect(() => {
    if (!shouldWatch) return undefined;

    const unsubscribe = subscribePhoneRelay(
      pairingId,
      (relay) => {
        if (!relay) {
          // The desktop consumed and deleted it, or a sweep ran.
          if (phaseRef.current === "recording") stopRef.current?.();
          setPhase((prev) => (prev === "done" ? prev : "expired"));
          return;
        }
        setClarifyingQuestions(relay.clarifyingQuestions);
        // The desktop answering a Next is a changed step label coming back down, so that is
        // also what releases the button. No separate ack field to keep in sync.
        //
        // Compared against a ref rather than inside a setState updater: updaters must be pure,
        // and this one was releasing the button as a side effect of one. It also keeps every
        // snapshot that does not touch `remote` — and there are more of those now that this
        // listener sees our own writes — from re-rendering the screen for nothing.
        applyRemoteRef.current(relay.remote);

        // The desktop has just pointed this phone at a note section. See dictateArmRef: the
        // post-round screen is the wrong screen for a round nobody has spoken yet, so the phone
        // returns to ready — same treatment as a re-armed pairing below, and for the same
        // reason. Only from "done": a round already running owns the screen, and its own target
        // was captured when it started.
        const armKey = dictateArmKey(relay.remote);
        const newArming = Boolean(armKey) && armKey !== dictateArmRef.current;
        dictateArmRef.current = armKey;
        // Falls through rather than returning, so a stale `stopping` left on the document is
        // still answered by the handlers below in the same snapshot.
        if (newArming && phaseRef.current === "done") {
          lastFlushedRef.current = "";
          // Or the ready screen opens showing the last round's elapsed time.
          resetRecordingRef.current?.();
          setRelayError("");
          setPhase("ready");
        }

        if (
          relay.status === PHONE_RELAY_STATUS.CONNECTED &&
          phaseRef.current === "done"
        ) {
          // The desktop started a new encounter on the same pairing (rearmPhoneRelay). The
          // clinician is holding a phone that says their last recording arrived, and the
          // truthful next screen is the ordinary ready one rather than an invitation to add
          // more to an encounter that is no longer on the desktop.
          lastFlushedRef.current = "";
          // Or the ready screen opens showing the last round's elapsed time.
          resetRecordingRef.current?.();
          setRelayError("");
          setPhase("ready");
          return;
        }
        if (relay.status === PHONE_RELAY_STATUS.STOPPING && !claimedRef.current) {
          // ⚠️ A STOP REQUEST FOR A ROUND THAT IS NOT OURS. Either it predates this round's
          // claim (see claimedRef) or it was left on the document by a desktop that asked to
          // stop a phone which had already stopped itself — the two writes crossed, and the
          // document was left armed with nobody to answer it. Left alone it ambushes the next
          // round; the honest answer is the one the phone would have given, which is that it is
          // not recording. `stopped` is also what the desktop consumes, and it has already
          // consumed this round, so this clears the wire without moving any text.
          if (!staleStopAnsweredRef.current) {
            staleStopAnsweredRef.current = true;
            writePhoneTranscript(pairingId, lastFlushedRef.current, { final: true }).catch(
              () => {},
            );
          }
          return;
        }

        // A new stopping episode re-arms the answer below. Cleared on any other status, so the
        // guard cannot outlive the stop it belongs to.
        if (relay.status !== PHONE_RELAY_STATUS.STOPPING) stopAnsweredRef.current = false;

        if (relay.status === PHONE_RELAY_STATUS.STOPPING) {
          // ⚠️ ANSWER FIRST, THEN STOP. The desktop arms a five-second "this phone is dead"
          // timer when it asks, and stopping is no longer instant: stopRecording() below waits
          // for the segments already out for transcription to come back, which can take longer
          // than that on a slow round trip. Without this write the desktop gave up mid-drain,
          // took the transcript as of the last flush and deleted the document, so the tail of
          // the encounter was lost and the final write below failed with NOT_FOUND.
          //
          // Any write does the job — the desktop only needs to see the document change while
          // the stop is outstanding — so this re-sends the text already sent rather than
          // racing the recorder for a newer one. See PHONE_RELAY_STOP_FLUSH_MS.
          //
          // ⚠️ ONCE PER STOP. This listener now receives our own writes back (see
          // subscribePhoneRelay), and answering the echo of the answer would write, echo,
          // write again — a loop, against Firestore, on a phone. `stop` below is already
          // idempotent on its own phase, so only the write needs guarding.
          if (!stopAnsweredRef.current) {
            stopAnsweredRef.current = true;
            writePhoneTranscript(pairingId, lastFlushedRef.current).catch(() => {});
          }
          stopRef.current?.();
        }
      },
      () => setPhase("expired"),
      // ⚠️ THE WHOLE POINT OF THE REMOTE IS THAT IT KEEPS UP. The desktop's answer to a press
      // arrives while this phone's own command write is still in flight more often than not,
      // and a listener that skips pending-write snapshots throws that answer away without ever
      // getting it again — the button keeps the old step's label and the clinician presses it
      // a second time. See subscribePhoneRelay.
      { includeLocalWrites: true },
    );

    return () => unsubscribe?.();
  }, [pairingId, shouldWatch]);

  /**
   * Ask the desktop to move: advance a step, or take the shortcut to the note.
   *
   * ⚠️ THE PHONE IS ASKING, NOT DOING. The desktop re-checks its own gate before acting, so a
   * press that was valid a snapshot ago and is not any more is simply ignored there. Nothing
   * here needs to know why — which is exactly why this page can stay ignorant of diagnoses.
   */
  // Where this phone's next round is going. Pushed down by the desktop; see DictationBanner.
  const dictateMode = remote?.mode === "dictate";
  const dictateLabel = remote?.dictateLabel || "your note";

  const sendCommand = useCallback(
    async (verb) => {
      if (!pairingId || sendingVerb) return;
      // The buttons as they stand at the moment of the press. The spinner runs until they are
      // no longer these.
      pressedKeyRef.current = remoteButtonKey(remote);
      setSendingVerb(verb);
      try {
        await writePhoneCommand(pairingId, verb);
      } catch {
        // The pairing is gone, or the write failed. Releasing the button is the honest response:
        // nothing happened, and the desktop is the thing that would have shown that it had.
        pressedKeyRef.current = null;
        setSendingVerb(null);
      }
    },
    [pairingId, sendingVerb, remote],
  );

  /**
   * Tap the microphone.
   *
   * ⚠️ THE ORDER HERE IS THE FIX FOR A REAL BUG, so do not tidy it back. This used to claim
   * the pairing and flip the phase to "recording" FIRST, then call startRecording — which is
   * where the microphone permission prompt lives. That left the screen showing "Recording"
   * with a live Stop button underneath an unanswered permission prompt, and a second tap
   * (dismissing the prompt, or just impatience) landed on Stop and ended a recording that had
   * never begun. The clinician saw the prompt kill their recording.
   *
   * So: ask for the microphone first, with the button visibly busy and therefore not
   * clickable, and only claim the pairing and change the phase once capture is genuinely
   * running. As a bonus the desktop's `recording` status now means the phone IS recording
   * rather than "is about to ask permission", and getUserMedia is no longer separated from
   * the tap by a Firestore round trip, which matters on Safari where audio setup is gated on
   * the user gesture.
   */
  const start = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      // The permission prompt sits inside here. False means it was refused or the microphone
      // is unavailable; the hook has already set its own error, so say nothing extra.
      const capturing = await recording.startRecording();
      if (!capturing) return;

      // ⚠️ THIS ROUND ONLY, AND NOTHING THAT CAME BEFORE IT. The phone used to seed the text it
      // had already sent back into the recorder, so that its next flush did not wipe the
      // desktop. That put the whole encounter back on the wire every round, and it lost
      // everything whenever the phone was reloaded mid-encounter — the seed lived only in this
      // tab's memory. The desktop holds what came before now and stitches this round onto it,
      // so the wire starts each round empty on purpose.
      lastFlushedRef.current = "";

      setPhase("recording");
      staleStopAnsweredRef.current = false;
      await claimPhoneRelay(pairingId);
      // Only now is a `stopping` on the document capable of being about THIS round.
      claimedRef.current = true;
    } catch {
      // The claim only rejects when the pairing itself is gone: torn down after the grace
      // window, swept, or the desktop tab closed. Firestore queues an ordinary network blip
      // rather than rejecting it. So this is not "please try again", it is "that code is
      // dead", and the expired screen is the one that says so and offers a way onward.
      await Promise.resolve(recording.stopRecording()).catch(() => {});
      setPhase("expired");
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }, [pairingId, recording]);

  // Redeeming is a sign-in in flight. Showing the "please sign in" screen underneath it would
  // flash the exact friction this scan exists to avoid.
  if (userLoading || redeeming) return <Loading page />;

  if (!pairingId) {
    return (
      <PageShell title="Nothing to record">
        <Alert severity="info">
          Please start from your computer, then scan the code it shows.
        </Alert>
      </PageShell>
    );
  }

  if (!user || user.isAnonymous) {
    return (
      <PageShell
        title="Please sign in first"
        subtitle="Sign in on this phone with the same account you are using on the computer, and you will come straight back here."
      >
        <Stack spacing={2}>
          {/* Only when the scan carried a sign-in that failed. Reaching this screen with no
              grant in the link at all is ordinary, and has nothing to explain. */}
          {grantFailed && (
            <Alert severity="info">
              The sign-in in that code had already expired, so please sign in here once.
            </Alert>
          )}
          <Button component={RouterLink} to="/auth" variant="contained" fullWidth>
            Sign in
          </Button>
        </Stack>
      </PageShell>
    );
  }

  if (phase === "wrongAccount") {
    return (
      <PageShell title={PAIRING_TITLE}>
        <Stack spacing={3}>
          <Alert severity="warning">
            This phone is signed in as {user.email}. If that is not the account on your
            computer, please sign in on this phone with that account. Otherwise the code has
            expired. Either way, please show a new code on your computer and scan it again.
          </Alert>
        </Stack>
      </PageShell>
    );
  }

  if (phase === "expired") {
    return (
      <PageShell title={PAIRING_TITLE}>
        <Stack spacing={3}>
          {/* Said once, in the alert. This is a state a phone can fall INTO mid-flow, when
              the desktop tears the pairing down, so it has to announce itself rather than
              rely on a heading having quietly changed. */}
          <Alert severity="warning">
            That code has expired. Please show a new code on your computer, then scan it again.
          </Alert>
          {/* The one thing this phone genuinely knows that the desktop cannot tell it. A
              clinic phone signed in to the wrong account otherwise hits a dead end wearing
              the wrong error. */}
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            Signed in as {user.email}. If that is not the account on the computer, please sign
            in with that account.
          </Typography>
          <Divider flexItem />
          {/* A dead pairing is the single most likely moment for "forget the desktop, I will
              just do this here", so this is exactly where the way out belongs. */}
          <NewEncounterLink caption="Opens ChartMind here. Nothing on your computer is affected." />
        </Stack>
      </PageShell>
    );
  }

  if (phase === "done") {
    // Segments recorded but not yet transcribed and flushed. Until this clears, the encounter
    // is on its way rather than delivered, and nothing on this screen may claim otherwise.
    // Either half means the text is not on the desktop yet: `finalizing` covers the stop
    // handler's own round trip, pendingSegments the audio still being transcribed.
    const sending = finalizing || recording.pendingSegments > 0;

    return (
      <PageShell title={PAIRING_TITLE}>
        <Stack spacing={3} alignItems="center">
          {/* The one thing that just happened, in the one element on the page that is built
              to be noticed. Said once: the heading no longer competes with it.

              ⚠️ IT DOES NOT SAY "ARRIVED" UNTIL IT HAS. Transcription is server-side and the
              last segments are still in flight when this screen first renders, so there is a
              real window between tapping stop and the desktop holding the final text. This
              used to announce success through it, with a spinner underneath contradicting
              the alert directly above. */}
          {relayError ? (
            <Alert severity="error" sx={{ width: "100%" }}>
              {relayError}
            </Alert>
          ) : sending ? (
            // The same button as everywhere else, with a spinner in it, rather than an alert.
            <Stack spacing={1.5} alignItems="center">
              <RecordButton busy ariaLabel="Sending to your computer" />
              <Typography variant="body2" color="text.secondary">
                Sending to your computer…
              </Typography>
            </Stack>
          ) : dictateMode ? (
            // ⚠️ NOT A SUCCESS ALERT. The round did land, but this phone is still armed on a note
            // section, and the next press goes there too — which is the thing worth saying on a
            // screen the clinician is most likely reading while deciding whether to speak again.
            <DictationBanner remote={remote} state="landed" />
          ) : (
            <Alert severity="success" sx={{ width: "100%" }}>
              Your recording is on your computer.
            </Alert>
          )}

          {/* Clinicians remember one more thing on the way out of the room, and walking back
              to the desktop for a fresh code to add a sentence is its own friction. The
              pairing outlives the round for exactly this, so there is no window to beat: it
              lasts as long as the desktop tab does. Once that closes the document is gone and
              tapping here lands on the expired screen rather than failing oddly. */}
          {!sending && (
            <>
              <RecordButton
                recording={false}
                busy={starting}
                onClick={start}
                ariaLabel="Record more"
              />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                {dictateMode
                  ? `Tap to add more to ${dictateLabel}.`
                  : "Tap to add more to this encounter."}
              </Typography>
            </>
          )}

          {/* The moment the remote is most useful: the recording is in, and the next thing the
              clinician wants is the desktop moved on — usually while they are still walking
              back to it. */}
          <DesktopRemote
            remote={remote}
            onCommand={(verb) => void sendCommand(verb)}
            sendingVerb={sendingVerb}
          />

          {/* By this phase the desktop has already consumed the transcript, so simply navigating
              away strands nobody. */}
          <Divider flexItem sx={{ width: "100%" }} />
          <NewEncounterLink caption="Opens ChartMind here. The encounter on your computer is not affected." />
        </Stack>
      </PageShell>
    );
  }

  // Now a genuine capability failure rather than a browser-brand verdict: transcription runs
  // server-side, so this only fires where MediaRecorder or the microphone itself is missing.
  if (!recording.isSupported) {
    return (
      <PageShell title="This browser cannot record">
        <Alert severity="info">
          Please try a different browser on your phone, then scan the code again.
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      /**
       * ⚠️ THE WHOLE SCREEN CHANGES ITS STORY IN DICTATE MODE, not just a banner somewhere under
       * the microphone. "Hold your phone near the patient, and your words appear on your
       * computer" is a promise about the ENCOUNTER, and reading it while dictating a paragraph of
       * a finished note is how a clinician concludes they are about to overwrite the transcript
       * and stops using the feature. The title and the subtitle are what gets read first, so they
       * are where this has to be said.
       */
      title={dictateMode ? `Add to ${dictateLabel}` : PAIRING_TITLE}
      subtitle={
        dictateMode
          ? isRecording
            ? `Recording into ${dictateLabel} on your computer.`
            // No subtitle before the first press: the info alert below carries the invitation,
            // and saying "tap the microphone" twice above one microphone reads as a stutter.
            : ""
          : isRecording
            ? "Recording. Hold your phone near the patient, and your words appear on your computer."
            : "Tap the microphone, then hold your phone near the patient."
      }
    >
      <Stack spacing={3} alignItems="center">
        {relayError && (
          <Alert severity="error" onClose={() => setRelayError("")} sx={{ width: "100%" }}>
            {relayError}
          </Alert>
        )}

        {recording.error && (
          <Alert
            severity="error"
            onClose={recording.clearError}
            sx={{ width: "100%" }}
          >
            {/* Recording itself survives a blip now: audio is captured locally and each
                segment retries. Only say the connection is the problem when it actually is. */}
            {typeof navigator !== "undefined" && navigator.onLine === false
              ? "Your phone has lost its connection. Recording continues, and the transcript will catch up once you reconnect."
              : recording.error}
          </Alert>
        )}

        {/* ⚠️ ABOVE THE MICROPHONE, NOT BELOW IT. Below, it is read after the decision to press
            has already been made — which is exactly the moment a clinician thinks they are about
            to record over the encounter. */}
        {/* "armed" here means nothing has been dictated into the section on this arming: the
            ready screen is the invitation to the first press, so it must not carry copy about a
            round that has landed. */}
        <DictationBanner remote={remote} state={isRecording ? "recording" : "armed"} />

        {/* The same button, size and clock as the desktop's Record step. The halo around it
            swells with the voice while recording: that is the "is this thing on" answer, the
            way current voice assistants give it, rather than a separate meter. */}
        <RecordButton
          recording={isRecording}
          busy={starting}
          onClick={isRecording ? stop : start}
          level={isRecording ? micLevel : undefined}
        />

        {/* ⚠️ NO TRANSCRIPT ON THIS SCREEN, DELIBERATELY. Transcription is server-side and
            arrives in segments, so any text here is inherently a couple of seconds behind the
            speaker, and watching for words that have not come yet made the feature feel broken.
            The clinician is holding this phone at a patient's mouth and looking at the patient;
            the transcript belongs on the desktop, where it gets read and edited. The halo on the
            button already answers "is this thing on", instantly. */}
        {isRecording && <RecordingClock seconds={recording.recordingTime} />}

        {/* Only ask for something we could not do ourselves. With the wake lock held the
            screen stays on by itself, so there is nothing to say: the heading above already
            says "Recording" and a chip repeating it was noise. This appears only in the case
            the lock was refused, where it is the one thing the clinician has to do for us. */}
        {isRecording && !screenHeld && (
          <Chip color="warning" size="small" label="Please keep this screen on" />
        )}

        {clarifyingQuestions.length > 0 && (
          // Pushed down by the desktop, which is already polling for them. The phone does not
          // run its own useClarifyingQuestions: that would bill a second set of LLM calls for
          // one encounter. This is the screen actually in the clinician's hand, so it is the
          // right place for them.
          <Paper variant="outlined" sx={{ width: "100%", p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Questions to ask
            </Typography>
            <List dense disablePadding>
              {clarifyingQuestions.map((question, idx) => (
                <ListItem
                  key={`${idx}-${question}`}
                  sx={{ alignItems: "flex-start", py: 0.5, px: 0 }}
                >
                  <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                    <HelpOutlineRounded fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={question}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* ⚠️ NEVER WHILE RECORDING. Advancing a step mid-encounter would run the differential
            against a transcript still being spoken, and this screen's job during a recording is
            to be a microphone. The desktop refuses it too (phoneRemote gates on captureActive);
            hiding it here means the clinician is not offered something that would be declined. */}
        {!isRecording && (
          <DesktopRemote
            remote={remote}
            onCommand={(verb) => void sendCommand(verb)}
            sendingVerb={sendingVerb}
          />
        )}

        {/* The same way out as the finished screen, kept quiet here so it does not compete
            with the microphone, and hidden outright while recording. This is the other moment
            a clinician realises they would rather just do the whole encounter on the phone. */}
        {!isRecording && (
          <Button component={RouterLink} to="/dashboard/chartmind" size="small">
            Or start a new encounter on this phone
          </Button>
        )}
      </Stack>
    </PageShell>
  );
};

export default PhoneRecordingPage;
